import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';
import type { TransactionItem } from 'app-components/flux/transactions/transactionsTypes';
import { List as ImmutableList, Map as ImmutableMap, Record, type RecordOf } from 'immutable';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import type { TransactionRecord } from '../transactions/transactionsTypes';
export const FREE_TO_SPEND_REDUCER_KEY = 'freeToSpendStore';

export const BreakdownTypeEnum = Object.freeze({
  INCOME: 'income',
  BILLS: 'bills',
  TRANSFERS: 'transfers',
  SUBSCRIPTIONS: 'subscriptions',
  GOALS: 'goals',
});
export type BreakDownType = 'income' | 'bills' | 'transfers' | 'subscriptions' | 'goals';

export type BreakDownData = {
  includeData: ImmutableList<TransactionRecord>;
  excludeData: ImmutableList<TransactionRecord>;
  excludedAmount: number;
  includedAmount: number;
};

export const ExpansionPanelItemTypeEnum = {
  TXN: 'txn',
  TRANSFER: 'transfer',
} as const;

export type ExpansionPanelItem =
  | {
      type: typeof ExpansionPanelItemTypeEnum.TXN;
      txnItem: TransactionItem;
      amount: number;
    }
  | {
      type: typeof ExpansionPanelItemTypeEnum.TRANSFER;
      txnItemArray: TransactionItem[];
      amount: number;
    };

export const RolloverTypeEnum = Object.freeze({
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  BOTH: 'BOTH',
  NONE: 'NONE',
});

export type RolloverType = 'POSITIVE' | 'NEGATIVE' | 'BOTH' | 'NONE' | undefined;

export const validRolloverStates: RolloverType[] = [
  RolloverTypeEnum.POSITIVE,
  RolloverTypeEnum.NEGATIVE,
  RolloverTypeEnum.BOTH,
];

export const GSFStageEnum = {
  INTRO: 'INTRO_GSF',
  INCOME: 'INCOME_GSF',
  BILLS: 'BILLS_GSF',
  PLANNED: 'PLANNED_GSF',
  OTHER: 'OTHER_GSF',
  COMPLETE: 'COMPLETE',
} as const;

export type GSFStage = (typeof GSFStageEnum)[keyof typeof GSFStageEnum];

export const GSF_STAGE_ORDER: GSFStage[] = [
  GSFStageEnum.INTRO,
  GSFStageEnum.INCOME,
  GSFStageEnum.BILLS,
  GSFStageEnum.PLANNED,
  GSFStageEnum.OTHER,
  GSFStageEnum.COMPLETE,
];

export type PlannedSpendingItem = {
  id: string;
  clientId?: string;
  createdAt?: string;
  modifiedAt?: string;
  name?: string;
  targetAmount: number;
  overwrittenTargetAmount?: number;
  calculatedSpentAmount?: number;
  resetRolloverAmount?: number;
  unsetResetRolloverAmount?: boolean;
  cumulativeRolloverAmount?: number;
  isHideExcludedFromUi?: boolean;
  filterId: string;
  recurring?: boolean;
  rolloverType: RolloverType;
  isDeleted?: boolean;
  txnIds?: string[];
  excludedTxnIds?: string[];
};

export type PlannedSpendingItems = ImmutableList<PlannedSpendingItem>;

// Consolidates data between a COA and a PlannedSpendingItem
export type PlannedSpendingConflict = {
  name: string;
  plannedId: string;
  parentId?: string;
  filterId: string;
};

export type FreeToSpendProps = {
  id?: string | null | undefined;
  clientId?: string | null;
  isDeleted?: boolean | null;
  createdAt?: string;
  modifiedAt?: string;
  userModifiedAt?: string;

  calculatedRolloverAmount?: number;

  isOtherSpendingHideExcludedFromUi?: boolean | null | undefined;

  calculatedIncomeAmount?: number;
  incomeTxnIds?: string[];
  excludedIncomeTxnIds?: string[];
  overwrittenIncomeAmount?: number | null;
  resetOverwrittenIncomeAmount?: boolean;

  calculatedBillsAmount?: number;
  billsTxnIds?: string[];
  excludedBillsTxnIds?: string[];
  overwrittenBillsAmount?: number | null;
  resetOverwrittenBillsAmount?: boolean;

  calculatedTransferAmount?: number;
  transferTxnIds?: string[];
  excludedTransferTxnIds?: string[];
  overwrittenTransferAmount?: number | null;
  resetOverwrittenTransferAmount?: boolean;

  calculatedSubscriptionsAmount?: number | null;
  subscriptionsTxnIds?: string[];
  excludedSubscriptionsTxnIds?: string[];
  overwrittenSubscriptionsAmount?: number | null;
  resetOverwrittenSubscriptionsAmount?: boolean;

  calculatedGoalsAmount?: number | null;
  goalsIds?: string[];
  excludedGoalsIds?: string[];
  overwrittenGoalsAmount?: number | null;
  resetOverwrittenGoalsAmount?: boolean;

  spentAmount?: number;
  spentTxnIds?: string[];
  excludedSpentTxnIds?: string[];
  totalToSpendAmount?: number;
  leftToSpendAmount?: number;
  date?: string;

  calculatedPlannedSpendingAmount?: number | null;
  showClosedOut?: boolean;
  isClosedOut?: boolean;
  newMonthViewed?: boolean;
  plannedSpendingItems?: PlannedSpendingItems;
  projectedOtherSpending?: number | null;
  projectionType?: string | null;
  projectionStartDate?: string | null;
  projectionEndDate?: string | null;
  projectionBuffer?: number | null;
};

export type PlannedSpendingCardData = PlannedSpendingItem & {
  target: number;
  showUnused: boolean;
  leftToSpend: number;
  overSpent: boolean;
};

const emptyPlannedList = ImmutableList<PlannedSpendingItem>();

export type FreeToSpendPropsImmu = Omit<FreeToSpendProps, 'plannedSpendingItems'> & {
  plannedSpendingItems?: PlannedSpendingItems;
  date: string;
};

export type FreeToSpend = RecordOf<FreeToSpendPropsImmu>;

const FreeToSpendDefaults: FreeToSpendProps = Object.freeze({
  id: undefined,
  clientId: undefined,
  isDeleted: false,
  createdAt: undefined,
  modifiedAt: undefined,
  userModifiedAt: undefined,

  calculatedRolloverAmount: undefined,
  calculatedIncomeAmount: undefined,
  incomeTxnIds: [],
  excludedIncomeTxnIds: [],
  overwrittenIncomeAmount: null,
  resetOverwrittenIncomeAmount: false,

  calculatedBillsAmount: undefined,
  billsTxnIds: [],
  excludedBillsTxnIds: [],
  overwrittenBillsAmount: null,
  resetOverwrittenBillsAmount: false,

  calculatedTransferAmount: undefined,
  transferTxnIds: [],
  excludedTransferTxnIds: [],
  overwrittenTransferAmount: null,
  resetOverwrittenTransferAmount: false,

  calculatedSubscriptionsAmount: undefined,
  subscriptionsTxnIds: [],
  excludedSubscriptionsTxnIds: [],
  overwrittenSubscriptionsAmount: null,
  resetOverwrittenSubscriptionsAmount: false,

  calculatedGoalsAmount: undefined,
  goalsIds: [],
  excludedGoalsIds: [],
  overwrittenGoalsAmount: null,
  resetOverwrittenGoalsAmount: false,

  setAside: undefined,
  spentAmount: undefined,
  spentTxnIds: [],
  excludedSpentTxnIds: [],
  totalToSpendAmount: undefined,
  leftToSpendAmount: undefined,
  date: undefined,

  isOtherSpendingHideExcludedFromUi: undefined,
  calculatedPlannedSpendingAmount: undefined,
  showClosedOut: false,
  isClosedOut: false,
  newMonthViewed: undefined,
  plannedSpendingItems: undefined,
  projectedOtherSpending: 0,
  projectionType: undefined,
  projectionStartDate: undefined,
  projectionEndDate: undefined,
  projectionBuffer: undefined,
});

const freeToSpendFactory = Record<FreeToSpendPropsImmu>({
  ...FreeToSpendDefaults,
  plannedSpendingItems: emptyPlannedList,
  date: DateTime.local().startOf('month').toISODate(),
});

export const mkFreeToSpend = (f2s?: any): FreeToSpend =>
  freeToSpendFactory({
    clientId: uuidv4().toUpperCase() as string,
    ...(f2s?.toJS ? f2s.toJS() : f2s),
    plannedSpendingItems:
      (f2s?.plannedSpendingItems &&
        (ImmutableList.isList(f2s.plannedSpendingItems)
          ? f2s.plannedSpendingItems
          : ImmutableList(f2s.plannedSpendingItems))) ||
      emptyPlannedList,
  });

export type FreeToSpendPatch = FreeToSpendProps & {
  id: string;
  doPatch?: boolean;
};

export type FreeToSpendStore = QcsSyncResourceStore<FreeToSpendPropsImmu>;
export const mkFreeToSpendStore = Record<QcsSyncResourceStoreProps<FreeToSpendPropsImmu>>({
  resourcesById: ImmutableMap(),

  resourceName: 'freeToSpend',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [FREE_TO_SPEND_REDUCER_KEY]: FreeToSpendStore;
  }
}

export type PSICreatePayload = {
  id: string; // F2S parent object ID
  filterItems: any;

  // Partial PSI props
  name: string;
  targetAmount: number;
  recurring?: boolean;
  rolloverType: RolloverType;
  resetRolloverAmount?: number;
};
